import { useState } from 'react';
import { Box } from '@mui/material';
import {
  Breadcrumb,
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  FormSearchResultAdvanced,
  useDataProvider,
  useLocalization,
  useStore,
} from '@ui-framework/index';
import { useNavigate, useParams } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';
import { BatchFileImportProvider } from '@product-framework/components/BatchFileImportPage';
import BatchFileImportStatusSelector from '@product-framework/components/BatchFileImportStatusSelector';
import detailsSchema from '../../schemas/details.json';
import { detailsSearchSchema } from '../../schemas';
import { createApis, statusOptions } from '../list/config';
import validationSchema from './validations';
import {
  lineNumber,
  importRecordRaw,
  recordStatus,
  failedReason,
} from './accessorFunctions';

const STORE_NAME = 'ClaimPaymentImportRowsDetail';

function stripEmptyAndId(data) {
  const { id, ...other } = data;
  return Object.fromEntries(
    Object.entries({ ...other }).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );
}

function BatchFileImportRowsDetail() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const store = useStore();
  const { id: batchFileImportId } = useParams();

  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(null);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);
  const apis = createApis();

  const listApiUrl = apis.listDetails(dataProvider, batchFileImportId);

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => navigate('/dashboard'),
    },
    {
      name: formatMessage({
        id: 'LIST_CLAIM_PAYMENT_IMPORTS',
        defaultMessage: 'List Claim Payment Imports',
      }),
      icon: IconLibrary.ClaimPaymentExports,
      onClick: () => navigate('/claim-payment-import'),
    },
    {
      name: formatMessage({
        id: 'IMPORT_RECORD_DETAILS',
        defaultMessage: 'Import Record Details',
      }),
      icon: IconLibrary.View,
      onClick: undefined,
    },
  ];

  const searchToggleButton = {
    label: !showSearch
      ? formatMessage({
          id: 'SHOW_SEARCH',
          defaultMessage: 'Show Search',
        })
      : formatMessage({
          id: 'HIDE_SEARCH',
          defaultMessage: 'Hide Search',
        }),
    Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
    variant: 'contained',
    onClick: () => setShowSearch(!showSearch),
  };

  const stackablePageButtons = [searchToggleButton];

  const submitSearchForm = async data => {
    const requestData = stripEmptyAndId(data);
    const searchState = createSearchState(requestData, detailsSearchSchema);
    store.domain.list.set(STORE_NAME, searchState);
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: STORE_NAME,
    dataProvider,
    schema: detailsSchema,
    initialState: { showColumnFilters: false },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      lineNumber,
      importRecordRaw,
      recordStatus,
      failedReason,
    },
    sortBy: 'lineNumber',
    sortOrder: 'ASC',
    onRowCountChange: rowCount => setResultsFound(rowCount),
  };

  const searchProps = {
    customComponents: {
      ...FormFields,
      BatchFileImportStatusSelector,
    },
    storeName: STORE_NAME,
    IconLibrary,
    schema: detailsSearchSchema,
    onSubmit: submitSearchForm,
    onClear: () => store.domain.list.reset(STORE_NAME),
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => setShowSearch(false),
    getValueByControl: getDefaultValueByControl,
  };

  const searchValueFormatter = ({ name: searchName, value }) => {
    if (searchName === 'recordStatus') {
      const found = statusOptions.find(opt => opt.value === value);
      return found?.label || value;
    }
    return value;
  };

  const searchParamsProps = {
    preProcessSearchValues: x => x,
    storeName: STORE_NAME,
    schema: detailsSearchSchema,
    resultsFound,
    valueFormatter: searchValueFormatter,
  };

  return (
    <BatchFileImportProvider statusOptions={statusOptions}>
      <Layout>
        <Grid2 xs={12} container>
          <Grid2 xs={12} md={6}>
            <Breadcrumb items={breadCrumb} />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <StackablePageButtons buttons={stackablePageButtons} />
          </Grid2>
        </Grid2>
        <Grid2 xs={12}>
          <FormSearchResultAdvanced {...searchParamsProps} />
        </Grid2>

        {showSearch && (
          <PageSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: 'SEARCH_IMPORT_RECORD_DETAILS',
              defaultMessage: 'Search Import Record Details',
            })}>
            <Box>
              <CRUDComponent
                type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
                searchProps={searchProps}
              />
            </Box>
          </PageSection>
        )}

        <PageSection
          label={formatMessage({
            id: 'IMPORT_RECORD_DETAILS',
            defaultMessage: 'Import Record Details',
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
              listProps={listProps}
            />
          </Box>
        </PageSection>
      </Layout>
    </BatchFileImportProvider>
  );
}

export default observer(BatchFileImportRowsDetail);
